'use client';

import type {
  CompanyBasicResponse,
  CompanyContactResponse,
  CompanyFinancialsResponse,
  CompanyGesellschafterResponse,
  CompanyRegisterResponse,
} from '@/lib/backend-api';
import type { useSession } from '@/hooks/use-session';
import type { GuvFlatRow } from '@/new_components/guv/guvHistoryService';
import type { i18n } from 'i18next';
import type { CompanyProfileHeroData } from './CompanyProfileSection';
import type { buildRevenueHistory } from './companyDetailDerived';
import { ChartTableTabs } from './ChartTableTabs';
import {
  CompanyCostsPieChart,
  CompanyTrendChart,
  FINANCIAL_DOCS_ANCHOR,
  MOBILE_ORDER,
} from './companyDetailLayout';
import { FinancialHistoryTable } from './FinancialHistoryTable';
import { CompanyDetailRegisterDocs } from './CompanyDetailRegisterDocs';
import { ContactInformationCard } from './ContactInformationCard';
import { CompanyPeerSearchCard } from './CompanyPeerSearchCard';
import { FinancialDocsCard } from './FinancialDocsCard';
import { CostsBreakdownTable } from './CostsBreakdownTable';
import { ZeitverlaufTabbedCard } from './ZeitverlaufTabbedCard';
import { FundingCard, FundingCardPlaceholder } from './FundingCard';

interface CompanyDetailSidebarColumnProps {
  revenueHistory: ReturnType<typeof buildRevenueHistory>;
  mergedGuvData: GuvFlatRow[];
  heroData: CompanyProfileHeroData;
  hasRegisterDocs: boolean;
  effectiveRegisterData: CompanyRegisterResponse | null;
  registerDocumentsSource: CompanyRegisterResponse | CompanyGesellschafterResponse | null;
  gesellschafterData: CompanyGesellschafterResponse | null;
  basicData: CompanyBasicResponse | null;
  hasGesellschafterliste: boolean;
  euId: string;
  session: ReturnType<typeof useSession>['data'];
  loadingRegister: boolean;
  loadingGesellschafter: boolean;
  showRegisterAttribution: boolean;
  attributionRegisterId: string | null;
  contactData: CompanyContactResponse | null;
  effectiveFinancialsData: CompanyFinancialsResponse | null;
  canDownloadBilanz: boolean;
  hasCostBreakdown: boolean;
  i18n: i18n;
}

export function CompanyDetailSidebarColumn({
  revenueHistory,
  mergedGuvData,
  heroData,
  hasRegisterDocs,
  effectiveRegisterData,
  registerDocumentsSource,
  gesellschafterData,
  basicData,
  hasGesellschafterliste,
  euId,
  session,
  loadingRegister,
  loadingGesellschafter,
  showRegisterAttribution,
  attributionRegisterId,
  contactData,
  effectiveFinancialsData,
  canDownloadBilanz,
  hasCostBreakdown,
  i18n,
}: CompanyDetailSidebarColumnProps) {
  return (
    <div className="contents lg:flex lg:flex-col lg:order-2 lg:border-l lg:border-border min-w-0 lg:[&>*]:shrink-0">
      {revenueHistory.some(
        (e) =>
          e.totalRevenue != null ||
          e.totalBalanceSum != null ||
          e.profit != null ||
          e.totalEmployees != null,
      ) && (
        <ChartTableTabs
          className={MOBILE_ORDER.financialsChart}
          chart={
            <CompanyTrendChart
              revenueHistory={revenueHistory}
              guvData={mergedGuvData}
            />
          }
          table={
            <FinancialHistoryTable
              revenueHistory={revenueHistory}
              companyName={heroData.name}
            />
          }
        />
      )}

      {hasRegisterDocs && (
        <CompanyDetailRegisterDocs
          effectiveRegisterData={effectiveRegisterData}
          registerDocumentsSource={registerDocumentsSource}
          gesellschafterData={gesellschafterData}
          basicData={basicData}
          hasGesellschafterliste={hasGesellschafterliste}
          euId={euId}
          session={session}
          loadingRegister={loadingRegister}
          loadingGesellschafter={loadingGesellschafter}
          heroData={heroData}
          showRegisterAttribution={showRegisterAttribution}
          attributionRegisterId={attributionRegisterId}
        />
      )}

      {/* Contact info: website + social visible to all, emails gated */}
      <ContactInformationCard
        className={MOBILE_ORDER.contact}
        contact={contactData}
        basicData={basicData}
        showEmails={!!session}
        euId={euId}
      />

      <CompanyPeerSearchCard
        className={MOBILE_ORDER.peers}
        euId={basicData?.eu_id ?? euId}
        revenue={effectiveFinancialsData?.metrics?.[0]?.total_revenue}
        balanceSum={
          effectiveFinancialsData?.metrics?.[0]?.total_balance_sum ??
          effectiveFinancialsData?.metrics?.[0]?.passiva
        }
        naceCodes={
          effectiveRegisterData?.nace_codes ?? basicData?.nace_codes ?? []
        }
        similarCompanies={basicData?.similar_companies ?? []}
      />

      {/* Jahresabschlüsse above Kostenstruktur, Zeitverlauf and Finanzierung
          (mobile shows it right after the summary, via MOBILE_ORDER). */}
      <FinancialDocsCard
        className={MOBILE_ORDER.financialDocs}
        id={FINANCIAL_DOCS_ANCHOR}
        financialFiles={effectiveFinancialsData?.financial_files ?? []}
        euId={euId}
        isAuthenticated={!!session}
        canDownload={canDownloadBilanz}
      />

      {hasCostBreakdown && (
        <ChartTableTabs
          className={MOBILE_ORDER.costs}
          chart={
            <CompanyCostsPieChart guvData={mergedGuvData} />
          }
          table={<CostsBreakdownTable guvData={mergedGuvData} />}
        />
      )}

      {session && (
        <ZeitverlaufTabbedCard
          className={MOBILE_ORDER.zeitverlauf}
          mergedGuvData={mergedGuvData}
          revenueHistory={revenueHistory}
        />
      )}

      {session &&
        contactData?.enrichment &&
        (contactData.enrichment.funding_last_round_amount != null ||
          contactData.enrichment.funding_rounds != null ||
          contactData.enrichment.funding_investor_count != null ||
          contactData.enrichment.funding_last_round_type != null) && (
          <FundingCard
            className={MOBILE_ORDER.funding}
            linkedin={contactData.enrichment}
            language={i18n.language}
          />
        )}
      {!session && basicData?.has_funding_data && (
        <FundingCardPlaceholder
          className={MOBILE_ORDER.funding}
          euId={euId}
        />
      )}
      {/* Empty spacer to fill remaining height. Desktop only — below lg
          the wrapper is `display: contents`, so it would otherwise sit in
          the mobile stack as a grid row of its own. */}
      <div className="hidden lg:block grow" />
    </div>
  );
}
